import { isEmpty } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthProvider from "../context/AuthContext";
import { apiGet } from "../Utils/apiFetch";
import apiPath from "../Utils/apiPath";
import LayoutComponent from "../components/shared/Layout";
import LayoutNew from "../components/shared/LayoutNew";
import DesktopShell from "../components/shared/DesktopShell";

/**
 * Sports menu page — matches the reference exchange layout
 * (kingexch111.com/sports):
 *   • "Quick Links" bar + a 5-up icon row: In-Play, Multi Markets,
 *     Cricket, Soccer, Tennis
 *   • "All Sports" bar + a vertical chevron list of every sport.
 *
 * Styling is scoped (`spm-*` classes + an injected <style> block) so the
 * default sky-theme menu styles (black bg, colored underlines, green dots,
 * live badges) don't bleed in. Brand bar colour: #263e4e.
 * Quick-link icons come from /public/sports/*.svg.
 */

const BAR = "#263e4e";       // Quick Links / All Sports header bars
const BOX = "#14213d";       // quick-link icon boxes (darker blue)

function Sports() {
  const navigate = useNavigate();
  const { user, launchCasino } = useContext(AuthProvider);
  const [inplayCountData, setInplayCountData] = useState({});

  const inPlayCount = async () => {
    try {
      const { status, data: response_users } = await apiGet(apiPath.inPlayCount);
      if (status === 200 && response_users?.status && response_users?.data) {
        setInplayCountData(response_users.data);
      }
    } catch (e) {
      /* non-fatal — counts are decorative */
    }
  };

  useEffect(() => {
    inPlayCount();
  }, []);

  const openCasino = () => {
    if (!isEmpty(user)) {
      launchCasino({ platForm: "1", gameType: "", casinoType: "" });
    } else {
      navigate("/login");
    }
  };

  const quickLinks = [
    { label: "In-Play", icon: "/sports/inplay.svg", onClick: () => navigate("/inplay") },
    { label: "Multi Markets", icon: "/sports/multi.svg", onClick: () => navigate("/multiMarket") },
    { label: "Cricket", icon: "/sports/cricket.svg", onClick: () => navigate("/inplay") },
    { label: "Soccer", icon: "/sports/scorrer.svg", onClick: () => navigate("/inplay") },
    { label: "Tennis", icon: "/sports/tennis.svg", onClick: () => navigate("/inplay") },
  ];

  const allSports = [
    { label: "Cricket", onClick: () => navigate("/inplay") },
    { label: "Casino", onClick: openCasino },
    { label: "Tennis", onClick: () => navigate("/inplay") },
    { label: "Soccer", onClick: () => navigate("/inplay") },
    { label: "Horse Racing", onClick: () => navigate("/inplay") },
    { label: "Politics", onClick: () => navigate("/inplay") },
    { label: "Greyhound Racing", onClick: () => navigate("/inplay") },
    { label: "Basketball", onClick: () => navigate("/inplay") },
    { label: "Lottery", onClick: openCasino },
  ];

  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const pageContent = (
      <div className={`mian-wrap ${isEmpty(user) ? "spm-wrap-out" : "spm-wrap"}`}>
        <style>{spmStyles}</style>

        {/* ── Quick Links ─────────────────────────────── */}
        <div className="spm-bar">Quick Links</div>
        <ul className="spm-quick">
          {quickLinks.map((q) => (
            <li key={q.label}>
              <a href="javascript:void(0)" onClick={q.onClick}>
                <span className="spm-ico">
                  <img src={q.icon} alt={q.label} />
                </span>
                <span className="spm-ql-label">{q.label}</span>
              </a>
            </li>
          ))}
        </ul>

        {/* ── All Sports ──────────────────────────────── */}
        <div className="spm-bar spm-bar--sep">All Sports</div>
        <ul className="spm-list">
          {allSports.map((s) => (
            <li key={s.label}>
              <a href="javascript:void(0)" onClick={s.onClick}>
                <span>{s.label}</span>
                <span className="spm-arrow-box">
                  <i className="spm-chev" />
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
  );

  return isDesktop ? (
    <LayoutNew desktopHome>
      <DesktopShell activeNav="Sports">{pageContent}</DesktopShell>
    </LayoutNew>
  ) : (
    <LayoutComponent visibilityType={true}>{pageContent}</LayoutComponent>
  );
}

const spmStyles = `
.spm-wrap{
  /* The logged-in header (#headerMain1) is position:fixed, so content
     flows underneath it. Push the page down by the header height so the
     "Quick Links" bar isn't hidden behind the green header. */
  padding-top:14vw;
}
.spm-wrap-out{
  /* Logged-out header (login bar) is shorter + also fixed; smaller offset so
     the "Quick Links" bar sits right below it without a big gap. */
  padding-top:14vw;
}
.spm-bar{
  background:${BAR};
  color:#fff;
  text-align:center;
  font-size:3.80vw;         /* ~14.6px @ 393w, matches reference */
  font-weight:600;
  line-height:8.2vw;        /* ~32px bar height */
  padding:0 12px;
  margin:0;
  letter-spacing:.2px;
}
/* separator line on top of the "All Sports" bar */
.spm-bar--sep{
  border-top:1px solid rgba(255,255,255,.25);
}
.spm-quick{
  display:flex;
  margin:0;
  padding:0;
  list-style:none;
  background:${BOX};
}
.spm-quick li{
  flex:1 1 20%;
  list-style:none;
  border-right:1px solid rgba(255,255,255,.12);
}
.spm-quick li:last-child{ border-right:0; }
.spm-quick a{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:2vw;
  height:22vw;
  color:#fff;
  text-decoration:none;
  text-align:center;
  padding:2vw 1vw;
}
.spm-ico{ display:flex; align-items:center; justify-content:center; height:8vw; }
.spm-ico img{
  width:7vw;
  height:7vw;
  object-fit:contain;
  /* SVGs use fill:currentColor (black) — force them white so they
     show on the dark #14213d bar, matching the reference */
  filter:brightness(0) invert(1);
}
.spm-ql-label{ font-size:3.46667vw; font-weight:700; line-height:1.15; }

.spm-list{ margin:0 !important; padding:0 !important; list-style:none; background:#fff; }
.spm-list li:first-child{ border-top:0; }
.spm-list li{ border-bottom:1px solid #e0e6e6; list-style:none; }
.spm-list li:last-child{ border-bottom:0; }
.spm-list a{
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:1.6vw 4vw;
  color:#1d6fb8;
  font-size:4.26667vw;
  font-weight:700;
  text-decoration:none;
}
/* arrow sits inside a bordered box (reference style) */
.spm-arrow-box{
  display:flex;
  align-items:center;
  justify-content:center;
  width:7.5vw;
  height:7.5vw;
  border:1px solid #e0e6e6;
  border-radius:1.2vw;
  flex:0 0 auto;
}
.spm-chev{
  display:inline-block;
  width:2.2vw;
  height:2.2vw;
  border-top:2px solid #333;
  border-right:2px solid #333;
  transform:rotate(45deg);
  margin-left:-0.5vw;
}
`;

export default Sports;
